import axios from 'axios';
import { getJwtToken } from "../utils/auth";
import { translateToEnglish, translateToSpanish } from "../utils/translations";
import {getBaseUrl} from "../utils/apiClient";

const locationsApiClient = axios.create({
    baseURL: `${getBaseUrl()}/locations`,
});

// Interceptor para agregar el token JWT a cada solicitud
locationsApiClient.interceptors.request.use(async (config) => {
    const token = await getJwtToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    //config.headers['bypass-tunnel-reminder'] = 'true';
    return config;
}, (error) => {
    return Promise.reject(error);
});

// ✅ GET all locations
export const getLocations = async () => {
    try {
        const response = await locationsApiClient.get();
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const listLocationNames = async () => {
    try {
        const response = await locationsApiClient.get('/list-all');
        return response.data;
    } catch (error) {
        throw error;
    }
}

// ✅ GET locations by category (ya lo tenías)
export const getLocationsByCategory = async (type, name) => {
    try {
        const response = await locationsApiClient.get(`/${translateToEnglish(type)}/${name}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// ✅ GET a single location by name
export const getLocationByName = async (name) => {
    try {
        const response = await locationsApiClient.get(`/${name}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// ✅ CREATE a new location
export const createLocation = async (locationData) => {
    try {
        const response = await locationsApiClient.post(``, locationData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// ✅ UPDATE a location by name
export const updateLocation = async (name, locationData) => {
    try {
        const response = await locationsApiClient.put(`/${name}`, locationData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// ✅ DELETE a location by name
export const deleteLocation = async (name) => {
    try {
        const response = await locationsApiClient.delete(`/${name}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const countLocations = async () => {
    try {
        const response = await locationsApiClient.get(`/count`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const resetAllLocations = async () => {
    try {
        const response = await locationsApiClient.delete(`/reset-all`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const syncLocationCounters = async (locationName) => {
    try {
        const response = await locationsApiClient.post(`/sync-counters/${locationName}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// =============== NUEVAS FUNCIONES GEOGRÁFICAS ===============

// 🆕 Función para calcular si unas coordenadas están dentro de una location
export const isCoordinateWithinLocation = (userLat, userLng, location) => {
    // Verificar que la location tenga datos geográficos
    if (!location.centerCoordinates || !location.radiusMeters) {
        return false;
    }

    const { lat: centerLat, lng: centerLng } = location.centerCoordinates;
    const radiusMeters = location.radiusMeters;

    // Calcular distancia usando fórmula de Haversine
    const R = 6371000; // Radio de la Tierra en metros
    const φ1 = (userLat * Math.PI) / 180;
    const φ2 = (centerLat * Math.PI) / 180;
    const Δφ = ((centerLat - userLat) * Math.PI) / 180;
    const Δλ = ((centerLng - userLng) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c; // Distancia en metros

    return distance <= radiusMeters;
};

// 🆕 Función para encontrar la location que contiene las coordenadas del usuario (LOCAL)
export const findLocationByCoordinatesLocal = (userLat, userLng, locations) => {
    if (!locations || locations.length === 0) {
        return null;
    }

    // Buscar locations que contengan las coordenadas
    const matchingLocations = locations.filter(location =>
        isCoordinateWithinLocation(userLat, userLng, location)
    );

    if (matchingLocations.length === 0) {
        return null;
    }

    // Si hay múltiples matches, devolver la de mayor prioridad
    if (matchingLocations.length > 1) {
        return matchingLocations.reduce((prev, current) =>
            (current.priority || 0) > (prev.priority || 0) ? current : prev
        );
    }

    return matchingLocations[0];
};

// 🆕 Función para encontrar location por nombre (fuzzy matching)
export const findLocationByName = (locationIQName, locations) => {
    if (!locationIQName || !locations || locations.length === 0) {
        return null;
    }

    const normalizeText = (text) => {
        return text.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remover acentos
            .replace(/[^a-z0-9\s]/g, '') // Remover caracteres especiales
            .trim();
    };

    const normalizedLocationIQ = normalizeText(locationIQName);

    // 1. Búsqueda exacta por nombre
    let match = locations.find(location =>
        normalizeText(location.name) === normalizedLocationIQ
    );

    if (match) {
        return match;
    }

    // 2. Búsqueda si LocationIQ contiene el nombre de la location
    match = locations.find(location =>
        normalizedLocationIQ.includes(normalizeText(location.name))
    );

    if (match) {
        return match;
    }

    // 3. Búsqueda si el nombre de la location está contenido en LocationIQ
    match = locations.find(location =>
        normalizeText(location.name).split(' ').some(word =>
            word.length > 2 && normalizedLocationIQ.includes(word)
        )
    );

    if (match) {
        return match;
    }

    console.log(`❌ No se encontró match por nombre para: ${locationIQName}`);
    return null;
};

export default locationsApiClient;